/**
 * HR Admin auth store (Pinia) — Firebase email/password.
 *
 * Replaces the original Materio user store and the MSAL-based auth.
 * Components that need auth check `isAuthenticated`, `user`, or `idToken`.
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from "firebase/auth";

export const useAuthStore = defineStore("auth", () => {
  const user = ref(null);
  const idToken = ref(null);
  const loading = ref(true);
  const error = ref(null);

  const isAuthenticated = computed(() => !!user.value && !!idToken.value);

  // Role derived from Firebase custom claim (defaults to "viewer" if not set).
  const role = computed(() => user.value?.role ?? "viewer");
  const isAdmin = computed(() => role.value === "admin");

  // `ready` resolves once Firebase has checked the persisted session (or confirmed no session).
  // The router guard awaits this before making auth decisions.
  let _resolveReady;
  const ready = new Promise((resolve) => {
    _resolveReady = resolve;
  });

  /**
   * Initialise: restore auth state from Firebase on app boot.
   * Returns a promise that resolves once Firebase has checked the session.
   */
  function init() {
    const auth = getAuth();
    return new Promise((resolve) => {
      onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          const tokenResult = await firebaseUser.getIdTokenResult();
          user.value = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            role: tokenResult.claims?.role ?? "viewer",
          };
          idToken.value = tokenResult.token;
        } else {
          user.value = null;
          idToken.value = null;
        }
        loading.value = false;
        _resolveReady(); // signal the guard that auth is known
        resolve();
      });
    });
  }

  async function signIn(email, password) {
    error.value = null;
    loading.value = true;
    try {
      const auth = getAuth();
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const tokenResult = await cred.user.getIdTokenResult();
      user.value = {
        uid: cred.user.uid,
        email: cred.user.email,
        displayName: cred.user.displayName,
        role: tokenResult.claims?.role ?? "viewer",
      };
      idToken.value = tokenResult.token;
    } catch (err) {
      error.value =
        err.code === "auth/invalid-credential"
          ? "Invalid email or password."
          : err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function signOut() {
    const auth = getAuth();
    await firebaseSignOut(auth);
    user.value = null;
    idToken.value = null;
  }

  return {
    user,
    idToken,
    loading,
    error,
    isAuthenticated,
    role,
    isAdmin,
    ready,
    init,
    signIn,
    signOut,
  };
});
